import type { UnitOfMeasure } from '@obraplan/core/domain/schedule/unit-of-measure';
import type { Service } from '@obraplan/core/domain/takeoff/service';
import type { XlsxSheet } from '@/features/reports/xlsx';

/**
 * O catálogo de serviços como planilha: disciplina, serviço e unidade, uma linha cada.
 *
 * É a lista que sai da obra para quem orça e para quem compra — o que existe e em que
 * unidade se mede. Por isso só o que está **ativo**: o serviço desativado não é mais
 * escolhido por obra nenhuma, e listá-lo seria convidar alguém a orçar o que o sistema
 * recusa medir.
 *
 * A ordem é a da tela (disciplina na ordem do cadastro, serviço na ordem dentro dela), e
 * não a alfabética: é a ordem em que a obra acontece, de topografia a pintura externa.
 */

export interface EntradaDoCatalogo {
  readonly service: Pick<Service, 'name' | 'unitOfMeasureId' | 'isActive'>;
  readonly disciplineName: string;
}

const CABECALHO = ['Disciplina', 'Serviço', 'Unidade de medida'] as const;

export function catalogoEmPlanilha(
  entradas: readonly EntradaDoCatalogo[],
  unidades: readonly Pick<UnitOfMeasure, 'id' | 'name'>[],
): XlsxSheet {
  const unidadePorId = new Map(unidades.map((unidade) => [unidade.id, unidade.name]));
  return {
    name: 'Serviços',
    headers: CABECALHO,
    rows: entradas
      .filter((entrada) => entrada.service.isActive)
      .map(({ service, disciplineName }) => [
        disciplineName,
        service.name,
        // Vazio, e não "—": a célula vazia é o que o filtro do Excel separa como "(Vazias)",
        // e é assim que quem recebe acha os serviços que ainda não sabem em que medem.
        service.unitOfMeasureId === undefined
          ? ''
          : (unidadePorId.get(service.unitOfMeasureId) ?? ''),
      ]),
  };
}

/** `"servicos-2026-10-02.xlsx"` — o nome diz o que é e de quando. */
export function nomeDoArquivoDoCatalogo(agora: Date): string {
  return `servicos-${agora.toISOString().slice(0, 10)}.xlsx`;
}
